/**
 * /logout — cross-subdomain logout landing page (rebuild router).
 *
 * WHY THIS EXISTS / THE BUG IT FIXES
 * ──────────────────────────────────
 * The HR app (hr.aayatanveda.in) and Capture sign a user out of their OWN
 * local session, then redirect here so the PORTAL's Firebase Auth session is
 * ended too. Without a live /logout route the portal kept a persistent
 * (~90-day browserLocalPersistence) Firebase Auth session alive: on the next
 * HR open, HR bounced to /login, the still-signed-in portal minted a fresh
 * SSO handoff token, and HR auto-logged back in — so "logout didn't stick".
 *
 * The rebuild router (src/App.tsx) never registered a /logout route, so
 * `portal.aayatanveda.in/logout?return=…` fell through to <NotFound/>, which
 * neither signs out NOR honours `?return`. (The correct handler lived only in
 * the dead src/_legacy/ router.) This route restores it in the active app.
 *
 * FLOW
 *   1. `await signOut()` from the auth context (ends Firebase Auth) AND a
 *      direct `fbAuth.signOut()` (idempotent belt-and-braces) — BOTH awaited
 *      BEFORE any redirect, so there is no race where the redirect fires while
 *      the session is still alive and a handoff can still be minted.
 *   2. Honour `?return=<url>` — only AV-owned origins; append `?logout=1` so
 *      the destination shows its "signed out" screen and skips its
 *      portal-redirect boot path. Anything else → /login.
 *
 * SCOPE / SAFETY
 *   - Signing out here only clears Firebase Auth. The portal keeps NO separate
 *     session token in localStorage/sessionStorage/cookie (auth.tsx is driven
 *     purely by onAuthStateChanged), and the SSO mint (lib/handoff.ts) reads
 *     only `auth.currentUser` — so ending Firebase Auth fully stops re-mint.
 *   - This route is ONLY reached by explicit cross-subdomain logout redirects.
 *     The in-portal account-menu sign-out (AppShell) is unchanged and still
 *     goes signOut()→/login, so CLIENT logout is unaffected.
 *   - Normal "stay logged in" persistence is untouched: only this explicit
 *     /logout path clears the session.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { auth as fbAuth } from '../lib/firebase';

/** AV-owned origins that may receive the post-logout redirect. */
const ALLOWED_RETURN_ORIGINS = new Set([
  'https://aayatanveda.in',
  'https://aayatanveda.com',
  'https://portal.aayatanveda.in',
  'https://hr.aayatanveda.in',
  'https://capture.aayatanveda.in',
]);

function isSafeReturnUrl(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const u = new URL(raw);
    const origin = `${u.protocol}//${u.host}`;
    if (!ALLOWED_RETURN_ORIGINS.has(origin)) return null;
    return raw;
  } catch {
    return null;
  }
}

export default function Logout() {
  const { signOut } = useAuth();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const safeReturn = isSafeReturnUrl(params.get('return'));

    (async () => {
      // 1a. End the session via the auth context (awaits fbSignOut internally).
      try {
        await signOut();
      } catch {
        // non-fatal — fall through to the direct signOut below.
      }
      // 1b. Direct signOut — idempotent belt-and-braces so the persistent
      //     Firebase Auth session is definitely gone before we redirect. No
      //     handoff token can be minted once auth.currentUser is null.
      try {
        await fbAuth.signOut();
      } catch {
        // non-fatal
      }

      // 2. Redirect only AFTER sign-out has settled (no race).
      if (safeReturn) {
        const dest = new URL(safeReturn);
        dest.searchParams.set('logout', '1');
        window.location.replace(dest.toString());
      } else {
        window.location.replace('/login');
      }
    })();
  }, [signOut]);

  // Brief status — in practice visible for <1 s before the redirect.
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontFamily: 'system-ui, sans-serif',
        color: '#555',
        gap: '12px',
      }}
    >
      <p>Signing out…</p>
    </div>
  );
}
